import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Check, ChevronRight, Sparkles, ShieldCheck, Clock } from 'lucide-react';
import Layout from '@/components/Layout';
import CtaBand from '@/components/CtaBand';
import TrustRow from '@/components/TrustRow';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { api, fileUrl } from '@/lib/api';

const FAQ_DEFAULT = [
  { q: 'How fast can you start?', a: 'Most projects begin within 5–10 business days. Emergency work can often be same-day in our coverage areas.' },
  { q: 'Are you insured?', a: 'Yes — we carry general liability and worker’s comp coverage on every job.' },
  { q: 'Do you provide written warranties?', a: 'Every job includes our written workmanship warranty (1 year minimum, longer on remodels) plus the manufacturer’s warranty on materials.' },
  { q: 'Can I see real examples of your work?', a: 'Yes — view our Project Gallery, and we can share city-specific references during your free consultation.' },
];

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const [cat, setCat] = useState(null);

  useEffect(() => {
    api.get(`/services/${slug}`).then(r => setCat(r.data));
  }, [slug]);

  useEffect(() => {
    if (hash && cat) {
      const id = hash.replace('#', '');
      const el = document.getElementById(`sub-${id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hash, cat]);

  if (!cat) return <Layout><div className="max-w-7xl mx-auto px-6 py-20 text-slate-500">Loading...</div></Layout>;

  const lowMin = Math.min(...cat.sub_services.map(s => s.price_low));
  const highMax = Math.max(...cat.sub_services.map(s => s.price_high));

  return (
    <Layout>
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={fileUrl(cat.image)} alt={cat.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-28 text-white">
          <div className="text-xs uppercase tracking-[0.2em] text-[#EED7A6]">Service</div>
          <h1 className="mt-2 font-headline text-4xl md:text-6xl leading-[1.05]">{cat.name}</h1>
          <p className="mt-3 text-white/85 max-w-2xl">{cat.tagline}</p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <div className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#EED7A6]" /> Fully Insured</div>
            <div className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-[#EED7A6]" /> Same-week availability</div>
            <div className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#EED7A6]" /> AI ballpark in 60 sec</div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button asChild data-testid="service-detail-get-estimate-button" className="rounded-xl bg-[#C8922F] hover:bg-[#A87424] text-white h-12 px-6">
              <Link to={`/estimate?category=${cat.slug}`}><Sparkles className="h-4 w-4 mr-2" /> Get Free Estimate</Link>
            </Button>
            <Button asChild variant="outline" data-testid="service-detail-back-link" className="rounded-xl bg-white/10 border-white/30 text-white hover:bg-white/20 h-12 px-6">
              <Link to="/services">Browse all services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Starting Range</div>
            <div className="font-headline text-3xl text-[#17352D] mt-1">${lowMin.toLocaleString()} – ${highMax.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-1">depends on scope & finishes — final quote after free consultation.</div>
          </div>
          <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Service Area</div>
            <div className="font-headline text-xl text-[#17352D] mt-1">5 South Florida Counties</div>
            <div className="text-xs text-slate-500 mt-1">Palm Beach, Broward, Martin, St. Lucie, Miami-Dade.</div>
          </div>
          <div className="rounded-2xl border border-[#EED7A6] bg-[#FBF7EE] p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F]">Promise</div>
            <div className="font-headline text-xl text-[#17352D] mt-1">Premium craftsmanship, every job</div>
            <div className="text-xs text-slate-700 mt-1">1-year workmanship warranty + manufacturer coverage.</div>
          </div>
        </div>
      </section>

      {/* SUB-SERVICES LIST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <h2 className="font-headline text-3xl md:text-4xl text-[#17352D] mb-6">What we do under {cat.name}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cat.sub_services.map((s) => (
            <div key={s.slug} id={`sub-${s.slug}`} data-testid={`service-detail-subservice-${s.slug}`} className="flex items-center justify-between gap-4 rounded-2xl border border-[#E6E9EE] bg-white p-4 hover:shadow-[0_10px_30px_rgba(11,18,32,0.06)] transition-shadow-only">
              <div className="min-w-0">
                <div className="font-semibold text-[#17352D] truncate">{s.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">From ${s.price_low.toLocaleString()}{s.price_high > s.price_low && <> – ${s.price_high.toLocaleString()}</>} / {s.unit}</div>
              </div>
              <Button asChild size="sm" className="rounded-lg bg-[#17352D] hover:bg-[#1E4338] text-white">
                <Link to={`/estimate?category=${cat.slug}&sub=${s.slug}`}>Get Estimate</Link>
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">What's Included</div>
            <h2 className="font-headline text-3xl md:text-4xl text-[#17352D] mt-1">Concierge service from start to finish</h2>
            <ul className="mt-5 space-y-3 text-slate-700">
              {['Free on-site consultation & detailed written estimate', 'Material sourcing through our premium supplier network', 'Daily project updates via SMS and the customer portal', 'Full clean-up and disposal included', '1-year workmanship warranty (longer on remodels)'].map((b) => (
                <li key={b} className="flex gap-3"><Check className="h-5 w-5 text-[#17352D] flex-none mt-0.5" /> <span>{b}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-[#E6E9EE] overflow-hidden">
            <img src={fileUrl(cat.image)} alt={cat.name} className="w-full aspect-[4/3] object-cover" />
          </div>
        </div>
        <TrustRow className="mt-10" />
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold text-center">FAQ</div>
        <h2 className="font-headline text-3xl md:text-4xl text-[#17352D] text-center mt-1">Common questions</h2>
        <Accordion type="single" collapsible className="mt-8 space-y-3">
          {FAQ_DEFAULT.map((f, i) => (
            <AccordionItem key={i} value={`q${i}`} className="rounded-2xl border border-[#E6E9EE] bg-white px-4">
              <AccordionTrigger className="text-left font-semibold text-[#17352D] hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-700">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <CtaBand title={`Ready for ${cat.name.toLowerCase()}?`} />
    </Layout>
  );
}
